import { SITE_URL } from "./seo";
import { faqs } from "./site";

export interface FaqItem {
  q: string;
  a: string;
}

export interface FaqPillar {
  id: string;
  heading: string;
  items: FaqItem[];
}

/**
 * Unified FAQ source — pillars are derived from the same `faqs` list used on
 * every landing page (src/content/site.ts) so the /faq page and the LP FAQ
 * accordions always show identical questions and answers.
 */

// Slice boundaries mirror the comment-grouped sections in faqs (site.ts):
// 0–6   Booking & Payment        (7)
// 7–16  Yacht & Experience       (10)
// 17–23 Onboard, Children, Diet  (7)
// 24–29 Logistics & Weather      (6)
const PILLAR_SLICES: ReadonlyArray<readonly [number, number]> = [
  [0, 7],
  [7, 17],
  [17, 24],
  [24, 30],
];

const PILLAR_META: Record<"en" | "pt", { id: string; heading: string }[]> = {
  en: [
    { id: "booking", heading: "Booking & payment" },
    { id: "yacht", heading: "The yacht & experience" },
    { id: "onboard", heading: "Onboard, children & dietary" },
    { id: "logistics", heading: "Logistics & weather" },
  ],
  pt: [
    { id: "booking", heading: "Reserva e pagamento" },
    { id: "yacht", heading: "O iate e a experiência" },
    { id: "onboard", heading: "A bordo, crianças e dietas" },
    { id: "logistics", heading: "Logística e meteorologia" },
  ],
};

function buildPillars(lang: "en" | "pt"): FaqPillar[] {
  const list = faqs[lang];
  return PILLAR_META[lang].map((meta, i) => {
    const [start, end] = PILLAR_SLICES[i];
    return { ...meta, items: list.slice(start, end) };
  });
}

export const FAQ_EXPANDED: Record<"en" | "pt", FaqPillar[]> = {
  en: buildPillars("en"),
  pt: buildPillars("pt"),
};

/** JSON-LD FAQPage payload (flattens all 15 questions). */
export function faqExpandedLD(lang: "en" | "pt") {
  const items = FAQ_EXPANDED[lang].flatMap((p) => p.items);
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
}

/** BoatingBusiness / LocalBusiness combined block for the FAQ page. */
export function boatingBusinessLD() {
  return {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    additionalType: "https://schema.org/BoatingBusiness",
    "@id": `${SITE_URL}/#boating-business`,
    name: "Vilamoura Dreams Cruises — Morningstar Yacht",
    description:
      "Luxury private yacht charter from Marina de Vilamoura aboard Morningstar, a 200 m² power catamaran for up to 18 guests.",
    url: `${SITE_URL}/faq`,
    telephone: "+351939418375",
    priceRange: "€€€",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Marina de Vilamoura, Cais 21-22, Gate A2",
      addressLocality: "Quarteira",
      postalCode: "8125-409",
      addressRegion: "Algarve",
      addressCountry: "PT",
    },
    geo: { "@type": "GeoCoordinates", latitude: 37.0738, longitude: -8.1265 },
    areaServed: { "@type": "Place", name: "Algarve, Portugal" },
    hasMap: "https://maps.google.com/?q=Marina+de+Vilamoura+Cais+21+22",
    openingHours: "Mo-Su 08:00-21:00",
  };
}